import React, { useEffect, useRef } from 'react';
import { Checkbox } from '../Checkbox';
import { Radio } from '../Radio';
import { Text } from '../Text';
import { st, classes } from './Selector.st.css';
import { ExtraText } from './ExtraText';
import { ProgressBar } from './ProgressBar';
import { SelectorProps, SelectorToggleTooltipProps } from './Selector.types';
import { Tooltip } from '../Tooltip';

type SelectorComponent = React.ForwardRefExoticComponent<
  SelectorProps & React.RefAttributes<HTMLLIElement>
> & {
  ExtraText: typeof ExtraText;
  ProgressBar: typeof ProgressBar;
  displayName?: string;
  propTypes?: React.WeakValidationMap<SelectorProps>;
};

type InternalSelectorProps = SelectorProps & {
  /** Whether this item has virtual focus (aria-activedescendant) */
  isActive?: boolean;
};

const Selector = React.forwardRef<HTMLLIElement, InternalSelectorProps>(
  (
    {
      dataHook,
      size = 'medium',
      imageSize = 'large',
      imageShape = 'rectangular',
      image,
      toggleType = 'radio',
      showDivider = false,
      title,
      subtitle,
      extraNode,
      isSelected = false,
      isDisabled = false,
      toggleTooltipProps,
      indeterminate,
      showBelowNodeOnSelect,
      subtitleNode,
      belowNode,
      className,
      id,
      onToggle = i => i,
      isActive = false,
    }: InternalSelectorProps,
    forwardedRef,
  ) => {
    const rootRef = useRef<HTMLLIElement | null>(null);
    const toggleRef = useRef<any>(null);

    useEffect(() => {
      if (typeof forwardedRef === 'function') {
        forwardedRef(rootRef.current);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLLIElement | null>).current =
          rootRef.current;
      }
    });

    // Make the toggle input non-focusable (aria-activedescendant pattern)
    useEffect(() => {
      const toggle = toggleRef.current;
      if (!toggle) return;
      const wrappedRef = toggle.wrappedComponentRef;
      if (wrappedRef instanceof HTMLInputElement) {
        wrappedRef.tabIndex = -1;
        return;
      }
      const checkboxInput = wrappedRef?.inputRef?.current;
      if (checkboxInput) {
        checkboxInput.tabIndex = -1;
      }
    });

    const onClick = () => !isDisabled && onToggle(id);

    return (
      <li
        ref={rootRef}
        id={String(id)}
        data-hook={dataHook}
        className={st(classes.root, { size, showDivider, isActive }, className)}
        onClick={onClick}
        role="option"
        aria-selected={isSelected}
        data-disabled={isDisabled || undefined}
        data-shape={imageShape}
        data-size={imageSize}
        tabIndex={-1}
      >
        <div className={st(classes.mainPart, { disabled: isDisabled })}>
          <ConditionalTooltip
            toggleTooltipProps={!!isDisabled ? toggleTooltipProps : undefined}
          >
            {toggleType === 'checkbox' ? (
              <Checkbox
                ref={toggleRef}
                dataHook="toggle"
                checked={isSelected}
                disabled={isDisabled}
                indeterminate={indeterminate}
              />
            ) : (
              <Radio
                ref={toggleRef}
                dataHook="toggle"
                checked={isSelected}
                disabled={isDisabled}
              />
            )}
          </ConditionalTooltip>
          {image && (
            <div
              data-hook="selector-image"
              className={st(classes.image, {
                imageSize,
                imageShape,
              })}
              children={image}
            />
          )}

          <div className={classes.titles}>
            <Text
              dataHook="selector-title"
              size={size}
              ellipsis
              children={title}
            />

            {subtitle && (
              <Text
                size={size === 'medium' ? 'small' : 'tiny'}
                secondary
                dataHook="selector-subtitle"
                ellipsis
                children={subtitle}
              />
            )}

            {subtitleNode && (
              <div data-hook="subtitle-node">{subtitleNode}</div>
            )}
          </div>

          {extraNode && (
            <div
              className={classes.extra}
              data-hook="selector-extra-node"
              children={extraNode}
            />
          )}
        </div>

        {showBelowNodeOnSelect && belowNode && isSelected && (
          <div data-hook="below-section" className={classes.belowSection}>
            {belowNode}
          </div>
        )}
      </li>
    );
  },
) as SelectorComponent;

const ConditionalTooltip = ({
  toggleTooltipProps,
  children,
}: {
  toggleTooltipProps?: SelectorToggleTooltipProps;
  children: React.ReactNode;
}) => {
  return toggleTooltipProps ? (
    <Tooltip
      content={toggleTooltipProps?.content}
      disabled={false}
      dataHook="toggle-tooltip"
      enterDelay={toggleTooltipProps.transitionSettings?.openDelay}
      exitDelay={toggleTooltipProps.transitionSettings?.closeDelay}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
};

Selector.displayName = 'Selector';
Selector.ExtraText = ExtraText;
Selector.ProgressBar = ProgressBar;

export { Selector };
